import type { CSSProperties, ReactNode } from 'react';

import { ImageWithLoading } from '@/components/blocks/section-image/image-with-loading';
import { resolveSectionBackgroundConfig } from '@/components/page-sections/block-section-surface';
import type { IndexLayoutProps } from '@/components/page-sections/index-section-utils';
import { IndexSectionContainer } from '@/components/page-sections/index-section-views/shared';
import { PinnedSectionTextGroup } from '@/components/page-sections/pinned-section-text-group';
import { useShowPinnedSectionText } from '@/components/page-sections/use-show-pinned-section-text';
import { useDerivedContentTone } from '@/hooks/use-derived-content-tone';
import { useEffectiveColors } from '@/hooks/use-public-design-settings';
import { SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES } from '@/lib/derive-content-tone';
import { buildFocusPointStyle, readNumber } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import { CaseResultIndexVariant } from './definition';

function PinnedSurface({
  backgroundColor,
  className,
  children,
}: {
  backgroundColor?: string;
  className?: string;
  children: ReactNode;
}) {
  const style: CSSProperties | undefined = backgroundColor ? { backgroundColor } : undefined;
  const derivedTone = useDerivedContentTone(backgroundColor);
  const toneClassName = backgroundColor
    ? (derivedTone && SECTION_BACKGROUND_CONTENT_TONE_CLASSNAMES[derivedTone]) || 'text-foreground'
    : 'bg-primary text-primary-foreground';

  return (
    <div className={cn(toneClassName, className)} style={style}>
      {children}
    </div>
  );
}

export function CaseResultPinnedChrome({
  variant,
  layoutProps,
  backgroundProps,
  backgroundColor,
  pinnedHeading,
  pinnedDescription,
  children,
}: {
  variant: string;
  layoutProps: IndexLayoutProps;
  backgroundProps: Record<string, unknown>;
  backgroundColor?: string;
  pinnedHeading: string;
  pinnedDescription?: string;
  children: ReactNode;
}) {
  const showPinnedTextColumn = useShowPinnedSectionText(pinnedHeading, pinnedDescription);

  const pinnedText = showPinnedTextColumn ? (
    <PinnedSectionTextGroup pinnedHeading={pinnedHeading} pinnedDescription={pinnedDescription} />
  ) : null;

  if (variant === CaseResultIndexVariant.GRID_LAYOUT) {
    return (
      <PinnedSurface backgroundColor={backgroundColor}>
        <IndexSectionContainer layoutProps={layoutProps}>
          {pinnedText ? (
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] md:items-center">
              {pinnedText}
              {children}
            </div>
          ) : (
            children
          )}
        </IndexSectionContainer>
      </PinnedSurface>
    );
  }

  if (variant === CaseResultIndexVariant.LIST_LAYOUT) {
    return (
      <CaseResultListPinnedHero
        backgroundProps={backgroundProps}
        backgroundColor={backgroundColor}
        layoutProps={layoutProps}
        pinnedText={pinnedText}
      >
        {children}
      </CaseResultListPinnedHero>
    );
  }

  if (variant === CaseResultIndexVariant.SPLIT_LAYOUT) {
    return pinnedText ? (
      <div className="space-y-4">
        {pinnedText}
        {children}
      </div>
    ) : (
      children
    );
  }

  return children;
}

function CaseResultListPinnedHero({
  backgroundProps,
  backgroundColor,
  layoutProps,
  pinnedText,
  children,
}: {
  backgroundProps: Record<string, unknown>;
  backgroundColor?: string;
  layoutProps: IndexLayoutProps;
  pinnedText: ReactNode;
  children: ReactNode;
}) {
  const siteTextColor = useEffectiveColors()?.siteText;
  const background = resolveSectionBackgroundConfig(backgroundProps, siteTextColor);
  const hasImageBackground = !backgroundColor && background?.mode === 'image' && background.image;
  const bgFocusStyle = buildFocusPointStyle(
    readNumber(backgroundProps.backgroundFocusX) ?? 50,
    readNumber(backgroundProps.backgroundFocusY) ?? 50,
  );

  const content = (
    <IndexSectionContainer layoutProps={layoutProps}>
      <div className="mx-auto flex w-full flex-col items-center gap-8 text-center">
        {pinnedText}
        {children}
      </div>
    </IndexSectionContainer>
  );

  if (!hasImageBackground) {
    return <PinnedSurface backgroundColor={backgroundColor}>{content}</PinnedSurface>;
  }

  return (
    <div className="relative overflow-hidden text-white">
      <div className="absolute inset-0">
        <ImageWithLoading
          image={background.image}
          alt="Featured case results background"
          className="h-full w-full object-cover"
          style={bgFocusStyle}
          fallback={<div className="h-full w-full bg-primary" />}
        />
      </div>
      <div className="absolute inset-0 bg-background/45" />
      {content}
    </div>
  );
}
